import {
  SHOT_LIMITS,
  otherTeam,
  type LaunchSide,
  type ShotInput,
  type TeamId,
} from "@scb/protocol";
import {
  buildOccupancy,
  emptyPlateAccum,
  scoreShot,
  type PlateAccum,
  type RulesConfig,
  type ShotCapture,
} from "@scb/rules";
import { createRng, launchPointFor, simulateShot, type SimBall, type TableSpec } from "@scb/sim";

export type Difficulty = "easy" | "normal" | "hard";

export interface AiProfile {
  /** 발사 위치 후보 개수 */
  xSteps: number;
  /** 각도 후보 개수 */
  angleSteps: number;
  /** 세기 후보 개수 */
  powerSteps: number;
  /** 그리드 지터 (셀 크기 대비 0~1). 같은 배치에서 항상 같은 샷만 치지 않게 한다. */
  jitter: number;
  /**
   * 조준 오차 (rad). **샷을 고른 뒤에** 적용하므로 예측이 어긋나면서 실수가 난다.
   * 이게 난이도의 핵심이다.
   */
  angleError: number;
  /** 세기 오차 (비율) */
  powerError: number;
  /** 상위 K개 중에서 무작위 선택. 1이면 항상 최선. */
  topK: number;
}

/**
 * 조준 오차는 **칸 여유(±13mm)** 와 **뱅크 경로 길이(약 4m)** 기준으로 잡았다.
 * 1 mrad 오차가 4m 경로 끝에서 4mm로 벌어지므로:
 *   hard  1.5 mrad ≈ 6mm  (여유 안 → 대체로 성공)
 *   normal 6 mrad  ≈ 24mm (여유 밖 → 절반쯤 실패)
 *   easy  20 mrad  ≈ 80mm (거의 실패, 운 좋으면 들어감)
 *
 * 칸 크기나 배치를 바꾸면 이 값들도 함께 조정해야 한다.
 * 격자 크기는 득점 구간이 좁아서 hard 기준 1000회 이상 시뮬해야 안정적으로 찾는다.
 */
export const AI_PROFILES: Record<Difficulty, AiProfile> = {
  easy: {
    xSteps: 4,
    angleSteps: 9,
    powerSteps: 6,
    jitter: 0.8,
    angleError: 0.02,
    powerError: 0.06,
    topK: 6,
  },
  normal: {
    xSteps: 6,
    angleSteps: 13,
    powerSteps: 9,
    jitter: 0.5,
    angleError: 0.006,
    powerError: 0.015,
    topK: 2,
  },
  hard: {
    xSteps: 8,
    angleSteps: 17,
    powerSteps: 13,
    jitter: 0.25,
    angleError: 0.0015,
    powerError: 0.003,
    topK: 1,
  },
};

/**
 * 탐색 범위.
 *
 * 빗의 아래 턱이 막혀 있어 정면 샷은 절대 득점할 수 없다. 득점하려면 양옆 통로로
 * 크게 돌아 올라간 뒤 경사를 타고 내려와야 하므로, **벽 가까이 + 큰 각도 + 센 세기**가
 * 핵심 구간이다. 범위를 좁게 잡으면 AI가 득점 샷을 아예 못 찾는다.
 */
const SEARCH = {
  minPower: 2.4,
  maxPower: 6.5,
  maxAngle: (58 * Math.PI) / 180,
  edgeMargin: 0.08,
} as const;

export interface ChooseShotArgs {
  table: TableSpec;
  /** 현재 테이블 위 공들 */
  balls: readonly SimBall[];
  team: TeamId;
  config: RulesConfig;
  /**
   * 실제 샷에 쓰일 시드. 평가도 같은 시드로 해야 예측이 실제와 일치한다.
   * (결정론 시뮬레이션이라 같은 시드 + 같은 입력 → 같은 결과)
   */
  seed: number;
  /** 후보 생성용 시드. 실제 샷 시드와 분리해야 매번 같은 후보만 보지 않는다. */
  searchSeed: number;
  ballId: number;
  profile: AiProfile;
  /** 라운드 누적 판별 점수. Grand Slam 가치를 정확히 평가하는 데 쓰인다. */
  plateAccum?: readonly [PlateAccum, PlateAccum];
  /**
   * 이번 라운드에 AI가 쓰는 자리. 주면 그 자리의 고정 지점에서만 쏜다.
   * 생략하면 테이블 전체에서 발사 위치를 탐색한다 (자리 고정을 끈 설정·연습용).
   */
  launchSide?: LaunchSide;
}

interface Candidate {
  shot: ShotInput;
  /** 내 점수 - 상대 점수 */
  value: number;
}

/**
 * 후보 샷들을 실제로 시뮬레이션해보고 가장 점수가 높은 걸 고른다.
 *
 * 물리가 결정론적이라 "이 샷을 치면 몇 점"을 정확히 알 수 있다 —
 * 그래서 별도의 평가 함수나 학습 없이 완전 탐색만으로 꽤 센 AI가 된다.
 * 난이도는 탐색 폭(grid 크기)과 **고른 뒤 얹는 조준 오차**로 조절한다.
 *
 * 사이드 스핀은 탐색하지 않는다 (4차원이 되면 비용이 커진다). 스핀을 쓰는
 * 고급 플레이는 아직 AI의 영역이 아니다 — docs/05-roadmap.md M2 참고.
 */
export function chooseShot(args: ChooseShotArgs): ShotInput {
  const { table, balls, team, config, seed, ballId, profile } = args;
  const rng = createRng(args.searchSeed);
  const before = buildOccupancy(table, balls);
  const plateAccum = args.plateAccum ?? [emptyPlateAccum(), emptyPlateAccum()];
  const opponent = otherTeam(team);

  // 자리가 고정된 대전에서는 발사 지점이 한 점뿐이다. 서버가 다른 x를 클램프하지
  // 않고 **거부**하므로 여기서 지켜야 한다. 격자도 1칸으로 줄여 헛돌지 않게 한다.
  const seatX = args.launchSide ? launchPointFor(table, args.launchSide) : null;
  const xMin = seatX ?? SEARCH.edgeMargin;
  const xMax = seatX ?? table.width - SEARCH.edgeMargin;
  const xSteps = seatX === null ? profile.xSteps : 1;

  /**
   * x 탐색이 통째로 사라지면 후보 수가 `xSteps`배 줄어 AI가 눈에 띄게 약해진다.
   * (자리 고정 도입 직후 hard의 득점률이 8번 중 6번 → 4번으로 떨어졌다.)
   *
   * 그 예산을 **각도**로 옮긴다. 고정 자리에서는 득점 각도가 좁고 세기는 너그러워서
   * (한 각도에서 통하는 power가 수십 개) 각도 해상도가 그대로 실력이 된다.
   * 총 후보 수는 자리 고정 전과 같으므로 탐색 비용도 그대로다.
   */
  const angleSteps =
    seatX === null ? profile.angleSteps : profile.angleSteps * profile.xSteps;

  const candidates: Candidate[] = [];

  for (let xi = 0; xi < xSteps; xi++) {
    for (let ai = 0; ai < angleSteps; ai++) {
      for (let pi = 0; pi < profile.powerSteps; pi++) {
        const shot: ShotInput = {
          x: gridValue(xMin, xMax, xi, xSteps, profile.jitter, rng.next()),
          angle: gridValue(
            -SEARCH.maxAngle,
            SEARCH.maxAngle,
            ai,
            angleSteps,
            profile.jitter,
            rng.next(),
          ),
          power: gridValue(
            SEARCH.minPower,
            SEARCH.maxPower,
            pi,
            profile.powerSteps,
            profile.jitter,
            rng.next(),
          ),
          spin: 0,
        };

        // sampleHz를 낮춰 탐색 중 리플레이 프레임 생성 비용을 줄인다.
        const sim = simulateShot({
          table,
          balls,
          shot,
          ballId,
          team,
          seed,
          sampleHz: 4,
        });

        const byId = new Map<number, SimBall>();
        for (const b of sim.balls) byId.set(b.id, b);

        const captures: ShotCapture[] = [];
        for (const ev of sim.events) {
          if (ev.type !== "capture") continue;
          const ball = byId.get(ev.ballId);
          if (!ball) continue;
          captures.push({
            ballId: ev.ballId,
            team: ball.team,
            astrolabe: ev.astrolabe,
            slot: ev.slot,
          });
        }

        const scored = scoreShot({ config, before, captures, plateAccum });
        const value = scored.scores[team].total - scored.scores[opponent].total;
        candidates.push({ shot, value });
      }
    }
  }

  // 동점일 때 순서 의존성이 생기지 않게 value → power 순으로 안정 정렬한다.
  candidates.sort((a, b) => b.value - a.value || a.shot.power - b.shot.power);

  const pool = Math.max(1, Math.min(profile.topK, candidates.length));
  const chosen = candidates[profile.topK <= 1 ? 0 : rng.int(pool)];

  // 조준 오차는 고른 뒤에 얹는다 → 예측과 결과가 어긋나면서 사람처럼 빗나간다.
  return {
    x: chosen.shot.x,
    angle: clamp(
      chosen.shot.angle + rng.signed() * profile.angleError,
      -SHOT_LIMITS.maxAngle,
      SHOT_LIMITS.maxAngle,
    ),
    power: clamp(
      chosen.shot.power * (1 + rng.signed() * profile.powerError),
      SHOT_LIMITS.minPower,
      SHOT_LIMITS.maxPower,
    ),
    spin: 0,
  };
}

/** i번째 격자점. steps가 1이면 중앙값. jitter만큼 셀 안에서 흔든다. */
function gridValue(
  min: number,
  max: number,
  i: number,
  steps: number,
  jitter: number,
  random: number,
): number {
  if (steps <= 1) return (min + max) / 2;
  const cell = (max - min) / steps;
  const base = min + cell * (i + 0.5);
  return clamp(base + (random * 2 - 1) * cell * 0.5 * jitter, min, max);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
